import { roundToDecimals } from '@dolfin/utils/numbers'
import { format, isFuture, isValid, parseISO } from 'date-fns'
import { Tables } from './db_types'
import DolfinLoggerFactory from './logger/factory'
import DolfinRecord from './record'
import { DolfinRpc } from './rpc'
import { isValidCurrencyCode } from './utils'

export interface ExchangeInfo {
  query: {
    from: string
    to: string
    amount: number
  }
  info: {
    timestamp: number
    rate: number
  }
  date: string
  result: number
}

type CurrencyExchangeInfo = Pick<
  Tables<'organizations'>,
  'use_invoices_exchange_rates' | 'use_fixed_exchange_rates'
>

type CurrencyConversionOptions = {
  persistRate?: boolean
  strictDate?: boolean
  requireFixedRate?: boolean
}

const Logger = DolfinLoggerFactory.create()

class DolfinCurrency {
  public orgCurrencies: Map<number, string> = new Map()
  public teamCurrencies: Map<string, string> = new Map()
  private rateCache: Map<string, ExchangeInfo> = new Map()
  private readonly inFlightReadOnlyConversions = new Map<string, Promise<ExchangeInfo | null>>()
  private cachedConfig: CurrencyExchangeInfo | null = null

  constructor(
    private organizationId: number,
    private rpcClient: DolfinRpc
  ) {}

  private async loadOrgConfig(): Promise<CurrencyExchangeInfo | null> {
    if (!this.cachedConfig) {
      this.cachedConfig = await this.rpcClient.getOrgCurrencyExchangeConfig(this.organizationId)
    }

    return this.cachedConfig
  }

  async getOrgCurrency() {
    const currencyFromCache = this.orgCurrencies.get(this.organizationId)

    if (currencyFromCache) {
      return currencyFromCache
    } else {
      const currency = (await this.rpcClient.getOrgCurrency(this.organizationId)).currency
      this.orgCurrencies.set(this.organizationId, currency)
      return currency
    }
  }

  async getTeamCurrency(profileId: string, date: string, options?: { throwOnError?: boolean }) {
    const currencyFromCache = this.teamCurrencies.get(`${profileId}-${date}`)

    if (currencyFromCache) {
      return currencyFromCache
    } else {
      const currency = (await this.rpcClient.getTeam(profileId, this.organizationId, date, options))
        ?.currency

      if (!currency) {
        return null
      }

      this.teamCurrencies.set(`${profileId}-${date}`, currency)
      return currency
    }
  }

  validateCurrency(currency: string) {
    return isValidCurrencyCode(currency)
  }

  getFixedCurrencyRate(from: string, to: string, date: string) {
    return this.rpcClient.getFixedCurrencyRate(from, to, date, this.organizationId)
  }

  async convert(
    value: number,
    from: string,
    to?: string,
    date?: string,
    record?: DolfinRecord,
    options?: CurrencyConversionOptions
  ): Promise<ExchangeInfo | null> {
    // Share only read-only, record-independent requests with identical inputs.
    if (options?.persistRate === false && !record) {
      const key = JSON.stringify([
        value,
        from,
        to,
        date,
        options.strictDate,
        options.requireFixedRate
      ])
      const existing = this.inFlightReadOnlyConversions.get(key)
      if (existing) return existing
      const pending = this.convertInternal(value, from, to, date, record, options)
      this.inFlightReadOnlyConversions.set(key, pending)
      try {
        return await pending
      } finally {
        this.inFlightReadOnlyConversions.delete(key)
      }
    }
    return this.convertInternal(value, from, to, date, record, options)
  }

  private async convertInternal(
    value: number,
    from: string,
    to?: string,
    date?: string,
    record?: DolfinRecord,
    options?: CurrencyConversionOptions
  ) {
    await this.loadOrgConfig()

    const targetCurrency = to || (await this.getOrgCurrency())
    if (
      options?.strictDate &&
      (!date ||
        !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
        !isValid(parseISO(date)) ||
        date > new Date().toISOString().slice(0, 10))
    ) {
      throw new Error('Exchange rate date must be a valid date that is not in the future')
    }
    const exchangeDate =
      options?.strictDate && date
        ? date
        : date && !isFuture(parseISO(date)) && parseISO(date) > new Date('2020-01-01')
          ? date
          : format(new Date(), 'yyyy-MM-dd')
    Logger.info(`💰Converting ${value} from ${from} to ${targetCurrency} at ${exchangeDate}`)
    const isNegative = value < 0
    const absoluteValue = Math.abs(value)
    const applyOriginalSign = (amount: number) => (isNegative ? -amount : amount)
    const getConvertedResult = (rate: number) =>
      applyOriginalSign(roundToDecimals(absoluteValue * rate, 6))

    if (absoluteValue === 0) {
      return null
    }

    if (!this.validateCurrency(from) || !this.validateCurrency(targetCurrency)) {
      throw new Error('Invalid currency code')
    }

    const recordExchangeRate = record?.getInvoiceExchangeRate()
    const orgCurrency = await this.getOrgCurrency()
    // Invoice exchange rate is only for the org-currency pair (e.g. EUR→USD). Do not use it for other targets (EUR→GBP, etc.).
    const hasRecordSpecificRate =
      this.cachedConfig?.use_invoices_exchange_rates &&
      !!recordExchangeRate &&
      targetCurrency === orgCurrency

    // FIRST ATTEMPT - Get from cache (skip when record has invoice-specific rate – it's record-specific)
    if (
      !hasRecordSpecificRate &&
      !(options?.requireFixedRate && this.cachedConfig?.use_fixed_exchange_rates)
    ) {
      const cachedRateInfo = this.rateCache.get(`${from}-${targetCurrency}-${exchangeDate}`)
      if (cachedRateInfo) {
        return {
          ...cachedRateInfo,
          query: {
            ...cachedRateInfo.query,
            amount: value
          },
          result: getConvertedResult(cachedRateInfo.info.rate)
        }
      }
    }

    // SECOND ATTEMPT - Get from Invoices or DB (invoice rate only when converting to org currency)
    const currencyRateFromInvoiceOrDb = hasRecordSpecificRate
      ? recordExchangeRate!
      : this.cachedConfig?.use_fixed_exchange_rates
        ? await this.getFixedCurrencyRate(from, targetCurrency, exchangeDate)
        : null

    if (currencyRateFromInvoiceOrDb) {
      const exchangeRateInfo = {
        query: {
          from,
          to: targetCurrency,
          amount: value
        },
        info: {
          timestamp: new Date().getTime() / 1000,
          rate: currencyRateFromInvoiceOrDb
        },
        date: exchangeDate,
        result: getConvertedResult(currencyRateFromInvoiceOrDb)
      }

      // Cache by day – skip when rate is record-specific (invoice) to avoid polluting cache
      if (!hasRecordSpecificRate) {
        this.rateCache.set(`${from}-${targetCurrency}-${exchangeDate}`, exchangeRateInfo)
      }
      return exchangeRateInfo
    }

    if (
      options?.requireFixedRate &&
      this.cachedConfig?.use_fixed_exchange_rates &&
      from !== targetCurrency
    ) {
      throw new Error('Exchange rate is unavailable for the selected date')
    }

    if (process.env.USE_MOCK_EXCHANGE === 'true') {
      if (from === targetCurrency) {
        return {
          query: { from, to: targetCurrency, amount: value },
          info: { timestamp: Date.now() / 1000, rate: 1 },
          date: exchangeDate,
          result: value
        }
      }

      const mockRate = 1.23 // Mock rate for development
      Logger.info(`Using mock exchange rate of ${mockRate}`)
      return {
        query: { from, to: targetCurrency, amount: value },
        info: { timestamp: Date.now() / 1000, rate: mockRate },
        date: exchangeDate,
        result: getConvertedResult(mockRate)
      }
    }

    // THIRD ATTEMPT - Fetch from API
    let exchangeRateInfo: ExchangeInfo

    const exchangeRatesApiBaseUrl = process.env.EXCHANGE_RATES_API_BASE_URL
    if (exchangeRatesApiBaseUrl) {
      const exchangeRate = await fetch(
        `${exchangeRatesApiBaseUrl}/v1/convert?access_key=${process.env.EXCHANGE_API_KEY}&from=${from}&to=${targetCurrency}&amount=${absoluteValue}&date=${exchangeDate}`
      )
      if (!exchangeRate.ok) {
        throw new Error(`Failed to fetch exchange rate`)
      }
      exchangeRateInfo = (await exchangeRate.json()) as ExchangeInfo
    } else {
      throw new Error('Exchange rates API base URL is not set')
    }

    if (options?.persistRate !== false) {
      await this.rpcClient.saveCurrencyRate(
        from,
        targetCurrency,
        exchangeRateInfo.info.rate,
        `[${exchangeDate}, ${exchangeDate}]`
      )
    }

    // cache by day
    this.rateCache.set(`${from}-${targetCurrency}-${exchangeDate}`, exchangeRateInfo)

    return {
      ...exchangeRateInfo,
      query: {
        ...exchangeRateInfo.query,
        amount: value
      },
      result: applyOriginalSign(exchangeRateInfo.result)
    }
  }

  async shouldBeConverted(currency: string) {
    return currency !== (await this.getOrgCurrency())
  }

  /**
   * Converts a value from one currency to multiple target currencies.
   * This is more efficient than calling convert() multiple times as it
   * shares the cache and config loading.
   *
   * @param value - The value to convert
   * @param from - The source currency code
   * @param targetCurrencies - Array of target currency codes
   * @param date - The date to use for the exchange rate
   * @param record - Optional record for invoice-specific exchange rates (when use_invoices_exchange_rates is enabled)
   * @returns Map of currency code to ExchangeInfo
   */
  async convertToMultiple(
    value: number,
    from: string,
    targetCurrencies: string[],
    date: string,
    record?: DolfinRecord | null
  ): Promise<Map<string, ExchangeInfo>> {
    const results = new Map<string, ExchangeInfo>()

    if (value === 0 || targetCurrencies.length === 0) {
      return results
    }

    // Filter out the source currency (no conversion needed)
    const currenciesToConvert = targetCurrencies.filter(c => c !== from)

    // For the source currency, add a 1:1 "conversion"
    if (targetCurrencies.includes(from)) {
      results.set(from, {
        query: { from, to: from, amount: value },
        info: { timestamp: Date.now() / 1000, rate: 1 },
        date,
        result: value
      })
    }

    // Convert to each target currency
    for (const targetCurrency of currenciesToConvert) {
      try {
        const conversion = await this.convert(
          value,
          from,
          targetCurrency,
          date,
          record ?? undefined
        )
        if (conversion) {
          results.set(targetCurrency, conversion)
        }
      } catch (error) {
        Logger.error(`Failed to convert ${from} to ${targetCurrency}: ${error}`)
        // Continue with other conversions even if one fails
      }
    }

    return results
  }

  /**
   * Gets the active currencies for an organization from the database
   */
  async getActiveCurrencies(): Promise<string[]> {
    return this.rpcClient.getActiveCurrencies(this.organizationId)
  }
}

export default DolfinCurrency
